import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Brain, TrendingUp, TrendingDown, DollarSign, PieChart, 
  RefreshCw, Sparkles, Target, Shield, Zap, BarChart3,
  AlertTriangle, CheckCircle, ArrowUpRight, ArrowDownRight,
  Play, Loader2, ArrowRightLeft, Clock
} from "lucide-react";

const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);
const formatPercent = (v) => `${(v || 0).toFixed(2)}%`;

export default function AIAdvisor() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [rebalancing, setRebalancing] = useState(false);
  const [portfolio, setPortfolio] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [rebalanceResult, setRebalanceResult] = useState(null);
  const [rebalanceDialogOpen, setRebalanceDialogOpen] = useState(false);
  const [riskTolerance, setRiskTolerance] = useState("moderate");

  useEffect(() => {
    fetchPortfolioData();
  }, []);

  const fetchPortfolioData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/ai/advisor/portfolio-summary");
      setPortfolio(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load portfolio data");
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    toast.info("🧠 AI is analyzing your entire portfolio...");
    try {
      const res = await api.post("/ai/advisor/analyze", { risk_tolerance: riskTolerance });
      setAnalysis(res.data);
      toast.success("✅ Analysis complete!");
    } catch (e) {
      console.error(e);
      toast.error("Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const executeRebalance = async () => {
    setRebalancing(true);
    setRebalanceDialogOpen(true);
    toast.info("🔄 AI is calculating and executing rebalancing trades...");
    try {
      const res = await api.post("/ai/advisor/rebalance", { risk_tolerance: riskTolerance });
      setRebalanceResult(res.data);
      if (res.data.success) {
        toast.success(`✅ Rebalancing complete! ${res.data.trades_executed} trades executed.`);
        // Refresh portfolio data
        fetchPortfolioData();
      } else {
        toast.error(res.data.message || "Rebalancing failed");
      }
    } catch (e) {
      console.error(e);
      toast.error("Rebalancing failed");
      setRebalanceResult({ success: false, message: e.response?.data?.detail || "Unknown error" });
    } finally {
      setRebalancing(false);
    }
  };

  const getChangeColor = (value) => {
    if (value > 0) return "text-emerald-500";
    if (value < 0) return "text-red-500";
    return "text-zinc-400";
  };

  const getChangeIcon = (value) => {
    if (value > 0) return <ArrowUpRight className="w-4 h-4" />;
    if (value < 0) return <ArrowDownRight className="w-4 h-4" />;
    return null;
  };

  return (
    <Layout title="AI Investment Advisor">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Brain className="w-8 h-8 text-violet-500" />
              AI Investment Advisor
            </h1>
            <p className="text-zinc-400 mt-1">Unified portfolio analysis across all your investment accounts</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={riskTolerance} onValueChange={setRiskTolerance}>
              <SelectTrigger className="w-40 bg-[#0A0A0A] border-white/10 rounded-sm" data-testid="risk-tolerance-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="conservative">
                  <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-blue-400" /> Conservative</span>
                </SelectItem>
                <SelectItem value="moderate">
                  <span className="flex items-center gap-2"><Target className="w-4 h-4 text-amber-400" /> Moderate</span>
                </SelectItem>
                <SelectItem value="aggressive">
                  <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-red-400" /> Aggressive</span>
                </SelectItem>
              </SelectContent>
            </Select>
            <Button 
              onClick={fetchPortfolioData} 
              variant="outline" 
              className="border-white/10 rounded-sm"
              data-testid="refresh-portfolio-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button 
              onClick={runAnalysis} 
              disabled={analyzing || loading}
              className="bg-violet-500 hover:bg-violet-600 text-white rounded-sm"
              data-testid="run-analysis-btn"
            >
              {analyzing ? (
                <>
                  <Brain className="w-4 h-4 mr-2 animate-pulse" /> Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" /> Run AI Analysis
                </>
              )}
            </Button>
            <Button 
              onClick={executeRebalance} 
              disabled={rebalancing || loading || !portfolio?.accounts?.some(a => a.type === 'robinhood' || a.type === 'alpaca')}
              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm"
              data-testid="rebalance-btn"
            >
              {rebalancing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rebalancing...
                </>
              ) : (
                <>
                  <ArrowRightLeft className="w-4 h-4 mr-2" /> AI Rebalance
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Rebalance Dialog */}
        <Dialog open={rebalanceDialogOpen} onOpenChange={setRebalanceDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-emerald-500/30 rounded-none max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-emerald-500" /> AI Portfolio Rebalancing
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {rebalancing && (
                <div className="text-center py-8">
                  <Loader2 className="w-12 h-12 text-emerald-500 mx-auto mb-4 animate-spin" />
                  <p className="text-zinc-300">AI is analyzing and executing trades...</p>
                  <p className="text-sm text-zinc-500 mt-2">This may take a moment</p>
                </div>
              )}
              
              {rebalanceResult && !rebalancing && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className={`p-4 rounded-sm border ${rebalanceResult.success ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {rebalanceResult.success ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      )}
                      <span className={`font-medium ${rebalanceResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
                        {rebalanceResult.success ? 'Rebalancing Complete' : 'Rebalancing Failed'}
                      </span>
                    </div>
                    {rebalanceResult.message && (
                      <p className="text-sm text-zinc-300">{rebalanceResult.message}</p>
                    )}
                  </div>

                  {/* Stats */}
                  {rebalanceResult.success && (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 bg-white/5 border border-white/10 rounded-sm text-center">
                        <p className="text-2xl font-bold text-emerald-400">{rebalanceResult.trades_executed || 0}</p>
                        <p className="text-xs text-zinc-500">Trades Executed</p>
                      </div>
                      <div className="p-3 bg-white/5 border border-white/10 rounded-sm text-center">
                        <p className="text-2xl font-bold text-blue-400">{rebalanceResult.trades_planned || 0}</p>
                        <p className="text-xs text-zinc-500">Trades Planned</p>
                      </div>
                      <div className="p-3 bg-white/5 border border-white/10 rounded-sm text-center">
                        <p className="text-2xl font-bold text-amber-400">{rebalanceResult.trades_skipped || 0}</p>
                        <p className="text-xs text-zinc-500">Trades Skipped</p>
                      </div>
                    </div>
                  )}

                  {/* Trade Details */}
                  {rebalanceResult.trades && rebalanceResult.trades.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-zinc-300">Trade Details</p>
                      <div className="max-h-60 overflow-y-auto space-y-2">
                        {rebalanceResult.trades.map((trade, idx) => (
                          <div key={idx} className={`p-3 border rounded-sm ${trade.executed ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-zinc-500/5 border-zinc-500/20'}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 text-xs font-bold rounded ${trade.action === 'buy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                  {trade.action?.toUpperCase()}
                                </span>
                                <span className="text-white font-medium">{trade.symbol}</span>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-zinc-300">{trade.quantity} shares</p>
                                <p className="text-xs text-zinc-500">{formatCurrency(trade.value)}</p>
                              </div>
                            </div>
                            <p className="text-xs text-zinc-500 mt-1">{trade.account} • {trade.executed ? '✓ Executed' : '○ Skipped'}</p>
                            {trade.reason && <p className="text-xs text-zinc-400 mt-1">{trade.reason}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Reasoning */}
                  {rebalanceResult.ai_reasoning && (
                    <div className="p-3 bg-violet-500/10 border border-violet-500/30 rounded-sm">
                      <p className="text-xs text-violet-400 mb-1">AI Reasoning</p>
                      <p className="text-sm text-zinc-300 whitespace-pre-wrap">{rebalanceResult.ai_reasoning}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Portfolio Summary Cards */}
        {portfolio && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-mono uppercase text-zinc-500">Total Portfolio Value</p>
                  <DollarSign className="w-4 h-4 text-primary" />
                </div>
                <p className="text-2xl font-bold text-primary font-mono mt-2">{formatCurrency(portfolio.total_value)}</p>
                <div className={`flex items-center gap-1 text-sm mt-1 ${getChangeColor(portfolio.total_gain_loss)}`}>
                  {getChangeIcon(portfolio.total_gain_loss)}
                  <span>{formatCurrency(portfolio.total_gain_loss)} ({formatPercent(portfolio.total_gain_loss_percent)})</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-mono uppercase text-zinc-500">Active Accounts</p>
                  <BarChart3 className="w-4 h-4 text-blue-400" />
                </div>
                <p className="text-2xl font-bold text-white font-mono mt-2">{portfolio.accounts_count}</p>
                <p className="text-sm text-zinc-500 mt-1">{portfolio.total_positions} total positions</p>
              </CardContent>
            </Card>

            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-mono uppercase text-zinc-500">Available Cash</p>
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                </div>
                <p className="text-2xl font-bold text-emerald-400 font-mono mt-2">{formatCurrency(portfolio.total_cash)}</p>
                <p className="text-sm text-zinc-500 mt-1">{formatPercent(portfolio.cash_percent)} of portfolio</p>
              </CardContent>
            </Card>

            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-mono uppercase text-zinc-500">Diversification Score</p>
                  <PieChart className="w-4 h-4 text-amber-400" />
                </div>
                <p className="text-2xl font-bold text-amber-400 font-mono mt-2">{portfolio.diversification_score}/100</p>
                <p className="text-sm text-zinc-500 mt-1">{portfolio.diversification_rating}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Account Breakdown */}
        {portfolio && portfolio.accounts && (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-400" /> Account Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {portfolio.accounts.map((account, idx) => (
                  <div key={idx} className="p-4 border border-white/10 hover:border-white/20 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-white">{account.name}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        account.type === 'robinhood' ? 'bg-green-500/20 text-green-400' :
                        account.type === 'alpaca' ? 'bg-blue-500/20 text-blue-400' :
                        account.type === 'crypto' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {account.type}
                      </span>
                    </div>
                    <p className="text-xl font-bold text-primary font-mono">{formatCurrency(account.value)}</p>
                    <div className={`flex items-center gap-1 text-sm ${getChangeColor(account.gain_loss)}`}>
                      {getChangeIcon(account.gain_loss)}
                      <span>{formatCurrency(account.gain_loss)}</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">{account.positions_count} positions</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top Holdings */}
        {portfolio && portfolio.top_holdings && portfolio.top_holdings.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" /> Top Holdings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {portfolio.top_holdings.map((holding, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 border border-white/5 hover:border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center text-sm font-bold text-white">
                        {holding.symbol?.slice(0, 3)}
                      </div>
                      <div>
                        <p className="text-white font-medium">{holding.symbol}</p>
                        <p className="text-xs text-zinc-500">{holding.source} • {holding.quantity} shares</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-mono">{formatCurrency(holding.value)}</p>
                      <p className={`text-sm font-mono ${getChangeColor(holding.gain_loss)}`}>
                        {holding.gain_loss >= 0 ? '+' : ''}{formatCurrency(holding.gain_loss)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Analysis Results */}
        {analysis && (
          <Card className="bg-[#0A0A0A] border-violet-500/30 rounded-none ai-active">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-violet-500" /> AI Portfolio Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-violet-500/10 border border-violet-500/30 rounded-sm">
                  <p className="text-xs text-zinc-400">Risk Assessment</p>
                  <p className="text-lg font-bold text-violet-400">{analysis.risk_level}</p>
                </div>
                <div className="p-3 bg-violet-500/10 border border-violet-500/30 rounded-sm">
                  <p className="text-xs text-zinc-400">Portfolio Health</p>
                  <p className="text-lg font-bold text-violet-400">{analysis.health_score}/100</p>
                </div>
                <div className="p-3 bg-violet-500/10 border border-violet-500/30 rounded-sm">
                  <p className="text-xs text-zinc-400">Recommendations</p>
                  <p className="text-lg font-bold text-violet-400">{analysis.recommendations_count}</p>
                </div>
                <div className="p-3 bg-violet-500/10 border border-violet-500/30 rounded-sm">
                  <p className="text-xs text-zinc-400">Rebalance Needed</p>
                  <p className="text-lg font-bold text-violet-400">{analysis.needs_rebalance ? 'Yes' : 'No'}</p>
                </div>
              </div>

              {/* Warnings */}
              {analysis.warnings && analysis.warnings.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-amber-400 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Warnings
                  </p>
                  {analysis.warnings.map((warning, idx) => (
                    <div key={idx} className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm text-sm text-amber-200">
                      {warning}
                    </div>
                  ))}
                </div>
              )}

              {/* Recommendations */}
              {analysis.recommendations && analysis.recommendations.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-emerald-400 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Recommendations
                  </p>
                  {analysis.recommendations.map((rec, idx) => (
                    <div key={idx} className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-sm text-sm text-emerald-200">
                      {rec}
                    </div>
                  ))}
                </div>
              )}

              {/* Full Analysis */}
              <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-sm max-h-96 overflow-y-auto">
                <div className="text-sm text-zinc-300 whitespace-pre-wrap">{analysis.full_analysis}</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {loading && (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-12 text-center">
              <RefreshCw className="w-8 h-8 text-zinc-600 mx-auto mb-4 animate-spin" />
              <p className="text-zinc-400">Loading portfolio data...</p>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!loading && !portfolio && (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-12 text-center">
              <Brain className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">No investment data found. Add investments to get started!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
